import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ThemeConfig } from './theme.types';
import { FontService } from './font.service';
import { ICON_REGISTRY, ICON_REGISTRY_TOKEN } from '../../shared/svg-icon/icon-registry';

// PrimeNG Aura preset testreszabása: a ThemeConfig színeit leképezi a PrimeNG token-rendszerére,
// így a PrimeNG komponensek (Button, Input, stb.) automatikusan az alkalmazás palettáját használják.
export function createThemePreset(config: ThemeConfig) {
  const { colors: c } = config;

  return definePreset(Aura, {
    semantic: {
      primary: {
        50:  c.primary[50],
        100: c.primary[100],
        200: c.primary[200],
        300: c.primary[300],
        400: c.primary[400],
        500: c.primary[500],
        600: c.primary[600],
        700: c.primary[700],
        800: c.primary[800],
        900: c.primary[900],
        950: c.primary[950],
      },
      colorScheme: {
        light: {
          primary: {
            color:         c.primary[500],
            contrastColor: c.white,
            hoverColor:    c.primary[600],
            activeColor:   c.primary[700],
          },
          highlight: {
            background:      c.primary[50],
            focusBackground: c.primary[100],
            color:           c.primary[700],
            focusColor:      c.primary[800],
          },
          surface: {
            0:   c.surface[0],
            50:  c.surface[50],
            100: c.surface[100],
            200: c.surface[200],
            300: c.surface[300],
            400: c.surface[400],
            500: c.surface[500],
            600: c.surface[600],
            700: c.surface[700],
            800: c.surface[800],
            900: c.surface[900],
            950: c.surface[950],
          },
        },
      },
    },
  });
}

export function provideThemeCssVars(config: ThemeConfig): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: ICON_REGISTRY_TOKEN, useValue: { ...ICON_REGISTRY, ...config.icons } },
    provideAppInitializer(() => {
      const doc = inject(DOCUMENT) as Document;
      const fontService = inject(FontService);
      const root = doc.documentElement;
      const { colors: c } = config;

    const vars: Record<string, string> = {
      '--color-success':       c.success.default,
      '--color-success-light': c.success.light,
      '--color-success-dark':  c.success.dark,
      '--color-success-thin':  c.success.thin,

      '--color-info':          c.info.default,
      '--color-info-light':    c.info.light,
      '--color-info-dark':     c.info.dark,
      '--color-info-thin':     c.info.thin,

      '--color-warn':          c.warn.default,
      '--color-warn-light':    c.warn.light,
      '--color-warn-dark':     c.warn.dark,
      '--color-warn-thin':     c.warn.thin,

      '--color-danger':        c.danger.default,
      '--color-danger-light':  c.danger.light,
      '--color-danger-dark':   c.danger.dark,
      '--color-danger-thin':   c.danger.thin,

      '--color-text':          c.text.default,
      '--color-text-muted':    c.text.muted,
      '--color-text-disabled': c.text.disabled,

      '--fm-primary':          c.uiPrimary.default,
      '--fm-primary-light':    c.uiPrimary.light,
      '--fm-primary-hover':    c.uiPrimary.hover,
      '--fm-badge-bg':         c.badgeBg,
      '--fm-badge-radius':     c.badgeRadius,
      '--fm-accent':           c.accent,
      '--fm-accent-hover':     c.accentHover,
      '--fm-accent-text':      c.accentText,
      '--fm-outline-hover':    c.outlineHover,
      '--fm-header-bg':        c.headerBg,
      '--fm-panel-bg':         c.panelBg,
      '--fm-tree-selected-bg': c.treeSelectedBg,
      '--fm-tree-hover-bg':    c.treeHoverBg,
      '--fm-bg':               c.surface[100],
      '--fm-bg-light':         c.surface[50],
      '--fm-border':           c.surface[300],
      '--fm-border-light':     c.surface[200],
      '--fm-text':             c.text.default,
      '--fm-text-muted':       c.text.muted,
      '--fm-warning':                    c.warn.default,
      '--p-checkbox-icon-checked-color': c.checkboxIconCheckedColor,
    };

    if (config.font) {
      fontService.injectGoogleFont(config.font);
      vars['--fm-font-family'] = `'${config.font}', sans-serif`;
    }

    if (config.buttonTextTransform) {
      vars['--fm-button-text-transform'] = config.buttonTextTransform;
    }

    if (config.buttonFirstLetterTransform) {
      vars['--fm-button-first-letter-transform'] = config.buttonFirstLetterTransform;
    }

    // Az összes változót ráírjuk a :root-ra, így az egész alkalmazásban elérhetők CSS-ből
    for (const [key, value] of Object.entries(vars)) {
      root.style.setProperty(key, value);
    }
  }),
  ]);
}
